import axiosClient from "utils/axios";
import { ApiList, ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  AddUserRequest,
  User,
  UpdateUserRequest,
} from "features/Users/types/user.types";

export const getUsers = async ({
  page,
  limit,
  search,
}: {
  page?: number;
  limit?: number;
  search?: string;
} = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<User[]>>>(
      "/users",
      {
        params: {
          page,
          limit,
          search,
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching users: ", err);
    throw err;
  }
};

export const getUserOptions = async ({
  query,
  limit,
  page,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/users/options`,
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching User Options : ", err);
    throw err;
  }
};

/** Users with required employee profile; `value` = user id (same shape as legacy GET /employees/options for owner/assignee picks). */
export const getUserOptionsWithEmployeeProfile = async ({
  query,
  limit,
  page,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/users/options/with-employee-profile`,
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching user options (with employee profile): ", err);
    throw err;
  }
};

export type ChangeOwnPasswordBody = {
  currentPassword: string;
  newPassword: string;
};

/** Logged-in user only (`req.user`); `POST /users/change-password`. */
export const changeOwnPassword = async (body: ChangeOwnPasswordBody) => {
  const { data } = await axiosClient.post<ApiResponse<unknown>>(
    "/users/change-password",
    body,
  );
  return data;
};

export const createUser = async (payload: FormData) => {
  try {
    const response = await axiosClient.post<ApiResponse<AddUserRequest>>(
      "/users",
      payload,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating User : ", err);
    throw err;
  }
};

export const updateUser = async (id: number, payload: FormData) => {
  try {
    const response = await axiosClient.put<ApiResponse<UpdateUserRequest>>(
      `/users/${id}`,
      payload,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating User : ", err);
    throw err;
  }
};

export const getUser = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }

    const response = await axiosClient.get<ApiResponse<User>>(`/users/${id}`);
    return response.data;
  } catch (err) {
    console.warn("Error fetching User : ", err);
    throw err;
  }
};

export const deleteUser = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<User>>(
      `/users/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting User : ", err);
    throw err;
  }
};

export const setUserActiveStatus = async (id: number, isActive: boolean) => {
  const { data } = await axiosClient.patch<ApiResponse<unknown>>(
    `/users/${id}/active-status`,
    { isActive },
  );
  return data;
};
